import {
  supportGet,
  supportPost,
  supportPut,
  supportPatch,
} from "@/lib/http/support-client";
import type {
  ReportConfig,
  ReportConfigUpdate,
  ProblemReport,
  ProblemReportCreate,
  ProblemReportListResponse,
  ProblemReportUpdate,
  FieldCatalog,
  SupportCasesByTaskResponse,
  BulkStatusUpdate,
  BulkPriorityUpdate,
  BulkUpdateResult,
} from "./types";

// --- Field Catalog ---

export async function fetchFieldCatalog(
  token: string,
  vertical: string,
): Promise<FieldCatalog> {
  return supportGet<FieldCatalog>(
    `admin/report-config/${vertical}/catalog`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Report Config ---

export async function fetchReportConfig(
  token: string,
  vertical: string,
): Promise<ReportConfig | null> {
  return supportGet<ReportConfig | null>(
    `admin/report-config/${vertical}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function updateReportConfig(
  token: string,
  vertical: string,
  data: ReportConfigUpdate,
): Promise<ReportConfig> {
  return supportPut<ReportConfig>(
    `admin/report-config/${vertical}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Problem Reports ---

export async function createProblemReport(
  token: string,
  applicationId: number,
  data: ProblemReportCreate,
): Promise<ProblemReport> {
  return supportPost<ProblemReport>(
    `submissions/${applicationId}/reports`,
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function fetchProblemReports(
  token: string,
  params?: {
    vertical?: string;
    status?: string;
    priority?: string;
    report_id?: number;
    report_type?: "submission" | "carrier";
    archived?: boolean;
    limit?: number;
    offset?: number;
  },
): Promise<ProblemReportListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.vertical) searchParams.set("vertical", params.vertical);
  if (params?.status) searchParams.set("status", params.status);
  if (params?.priority) searchParams.set("priority", params.priority);
  if (params?.report_id) searchParams.set("report_id", String(params.report_id));
  if (params?.report_type) searchParams.set("report_type", params.report_type);
  if (params?.archived) searchParams.set("archived", "true");
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.offset) searchParams.set("offset", String(params.offset));
  const qs = searchParams.toString();
  return supportGet<ProblemReportListResponse>(
    `reports${qs ? `?${qs}` : ""}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function fetchProblemReport(
  token: string,
  reportId: number,
): Promise<ProblemReport> {
  return supportGet<ProblemReport>(
    `reports/${reportId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function updateProblemReport(
  token: string,
  reportId: number,
  data: ProblemReportUpdate,
): Promise<ProblemReport> {
  return supportPatch<ProblemReport>(
    `reports/${reportId}`,
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Bulk Updates ---

export async function bulkUpdateReportStatus(
  token: string,
  data: BulkStatusUpdate,
): Promise<BulkUpdateResult> {
  return supportPost<BulkUpdateResult>(
    "reports/bulk/status",
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function bulkUpdateReportPriority(
  token: string,
  data: BulkPriorityUpdate,
): Promise<BulkUpdateResult> {
  return supportPost<BulkUpdateResult>(
    "reports/bulk/priority",
    data,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Archive ---

export async function archiveReport(
  token: string,
  reportId: number,
): Promise<ProblemReport> {
  return supportPost<ProblemReport>(
    `reports/${reportId}/archive`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

export async function unarchiveReport(
  token: string,
  reportId: number,
): Promise<ProblemReport> {
  return supportPost<ProblemReport>(
    `reports/${reportId}/unarchive`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Support Cases (whole-case surface, LIB-161) ---

/**
 * Resolve a gateway `task_id` to the problem-report cases correlated to its
 * submission, each as a whole-case object (relationship + report + persisted
 * knowledge-layer insight). Powers the Email audit page's Report-a-Problem /
 * Jira links + status. Returns `{ task_id, cases: [] }` (not an error) when the
 * task has no report — the caller simply hides the controls.
 */
export async function fetchSupportCasesByTask(
  token: string,
  taskId: string,
): Promise<SupportCasesByTaskResponse> {
  return supportGet<SupportCasesByTaskResponse>(
    `support/cases/by-task/${encodeURIComponent(taskId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Submission Snapshot ---

export async function fetchReportSnapshot(
  token: string,
  reportId: number,
): Promise<{ url: string }> {
  return supportGet<{ url: string }>(
    `reports/${reportId}/snapshot`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Carrier Payloads (served by multi-rater backend) ---

import { httpGet } from "@/lib/http/fetch-client";

export async function fetchCarrierPayloads(
  token: string,
  applicationId: number,
  carrierName: string,
  coverageType: string,
): Promise<Record<string, string>> {
  return httpGet<Record<string, string>>(
    `submissions/${applicationId}/quote_results/${encodeURIComponent(carrierName)}/${encodeURIComponent(coverageType)}/payloads`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

// --- Ingestion Log by gateway task_id (served by multi-rater backend) ---

/**
 * Resolve a gateway task_id to its correlated submission's ingestion-log URL
 * (LIB-155). Powers the Email Gateway detail page's Ingestion Logs card. Returns
 * `url: null` when there is no correlated submission (e.g. an upload, which
 * carries no task_id) or the submission has no ingestion log — the caller
 * renders an empty state in that case rather than treating it as an error.
 */
export async function fetchIngestionLogByTask(
  token: string,
  taskId: string,
): Promise<{ application_id: number | null; url: string | null }> {
  return httpGet<{ application_id: number | null; url: string | null }>(
    `submissions/by-task/${encodeURIComponent(taskId)}/ingestion-log`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

/**
 * Read the direct ingestion-log object URL from the bound Multi-Rater
 * submission. Unlike the task-based lookup, this remains valid when a report
 * captured no task id or its historical task id is stale.
 */
export async function fetchIngestionLogBySubmission(
  token: string,
  applicationId: number,
): Promise<string | null> {
  const submission = await httpGet<{
    records?: Array<{ acord_type?: string | null; s3_url?: string | null }>;
  }>(`submissions/${applicationId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return (
    submission.records?.find((record) => record.acord_type === "LOG")?.s3_url ??
    null
  );
}
